import { FunctionComponent, useMemo } from 'react';
import { AuthQueryParamValues, getAuthUrl } from 'utils/url-generator';
import {
  CommunityApp,
  CommunityNavigation,
  LoggedInUser,
} from '@atlassian/learning-components';
import useLoggedInUser from 'utils/useLoggedInUser';
import useIsLoggedIn from 'utils/useIsLoggedIn';
import {
  CommunityAnalyticsClient,
  eventType,
  getCommunityLoginUrl,
  getCommunityLogoutUrl,
} from '@atlassian/community-library';
import {
  accountSwitcherIsActive,
  hlvVanillaMigrationIsActive,
  hlvVanillaPrereleaseUrlIsActive,
  newNavigationIsActive,
} from 'services/statsig/featureGates';
import { ENVIRONMENT, getEnvironment } from 'utils/environment';

function getLoginUrl(
  queryParamValues: AuthQueryParamValues,
  env?: ENVIRONMENT,
) {
  if (hlvVanillaMigrationIsActive()) {
    return getHlvVanillaLoginUrl(env);
  }
  return getCommunityLoginUrl(queryParamValues, env);
}

function getLogoutUrl(
  queryParamValues: AuthQueryParamValues,
  env?: ENVIRONMENT,
) {
  if (hlvVanillaMigrationIsActive()) {
    return getHlvVanillaLogoutUrl(env);
  }
  return getCommunityLogoutUrl(queryParamValues, env);
}

const HLV_LOGIN_URL_CONFIG = {
  [ENVIRONMENT.LOCAL]: {
    communityBaseUrl: 'https://community.dev.atlassian.com',
    identityLoginUrl: 'https://id.stg.internal.atlassian.com/login',
    identitySignupUrl: 'https://id.stg.internal.atlassian.com/signup',
    identityLogoutUrl: 'https://id.stg.internal.atlassian.com/logout',
  },
  [ENVIRONMENT.DEV]: {
    communityBaseUrl: 'https://community.dev.atlassian.com',
    identityLoginUrl: 'https://id.stg.internal.atlassian.com/login',
    identitySignupUrl: 'https://id.stg.internal.atlassian.com/signup',
    identityLogoutUrl: 'https://id.stg.internal.atlassian.com/logout',
  },
  [ENVIRONMENT.STAGING]: {
    communityBaseUrl: 'https://community.stg.atlassian.com',
    identityLoginUrl: 'https://id.stg.internal.atlassian.com/login',
    identitySignupUrl: 'https://id.stg.internal.atlassian.com/signup',
    identityLogoutUrl: 'https://id.stg.internal.atlassian.com/logout',
  },
  [ENVIRONMENT.PROD]: {
    communityBaseUrl: 'https://community.atlassian.com',
    identityLoginUrl: 'https://id.atlassian.com/login',
    identitySignupUrl: 'https://id.atlassian.com/signup',
    identityLogoutUrl: 'https://id.atlassian.com/logout',
  },
};

type AppButtonHrefOverrides = Partial<
  Record<CommunityApp, Partial<Record<'loggedIn' | 'loggedOut', string>>>
>;

const getHlvVanillaPathPrefix = (): '/forums' | '/new-hlv-forums' =>
  hlvVanillaPrereleaseUrlIsActive() ? '/new-hlv-forums' : '/forums';

const getHlvVanillaEntryBaseUrl = (communityBaseUrl: string): string =>
  `${communityBaseUrl}${getHlvVanillaPathPrefix()}`;

const getHlvVanillaForumsBaseUrl = (env: ENVIRONMENT): string => {
  const { communityBaseUrl } = HLV_LOGIN_URL_CONFIG[env];
  return `${communityBaseUrl}${getHlvVanillaPathPrefix()}`;
};

const getHlvVanillaAppButtonHrefOverrides = (
  env: ENVIRONMENT,
): AppButtonHrefOverrides | undefined => {
  if (!hlvVanillaMigrationIsActive()) {
    return undefined;
  }

  const forumsBaseUrl = getHlvVanillaForumsBaseUrl(env);

  return {
    [CommunityApp.FORUMS]: {
      loggedIn: forumsBaseUrl,
      loggedOut: forumsBaseUrl,
    },
  };
};

function getHlvVanillaLoginUrl(env?: ENVIRONMENT) {
  const { communityBaseUrl, identityLoginUrl } =
    HLV_LOGIN_URL_CONFIG[env as ENVIRONMENT];
  const entryBaseUrl = getHlvVanillaEntryBaseUrl(communityBaseUrl);
  const continueUrl = `${entryBaseUrl}/entry/signin?Target=${encodeURIComponent(
    entryBaseUrl,
  )}`;

  return `${identityLoginUrl}?application=community--entry-auth&continue=${encodeURIComponent(
    continueUrl,
  )}`;
}

function getHlvVanillaLogoutUrl(env?: ENVIRONMENT) {
  const { communityBaseUrl, identityLogoutUrl } =
    HLV_LOGIN_URL_CONFIG[env as ENVIRONMENT];
  const entryBaseUrl = getHlvVanillaEntryBaseUrl(communityBaseUrl);
  const targetUrl = getHlvVanillaEntryBaseUrl(communityBaseUrl);
  const continueUrl = `${entryBaseUrl}/entry/signout?Target=${encodeURIComponent(
    targetUrl,
  )}`;

  return `${identityLogoutUrl}?application=community--entry-auth&continue=${encodeURIComponent(
    continueUrl,
  )}`;
}

function getSignupUrl(
  queryParamValues: AuthQueryParamValues,
  env?: ENVIRONMENT,
) {
  if (hlvVanillaMigrationIsActive()) {
    const { communityBaseUrl, identitySignupUrl } =
      HLV_LOGIN_URL_CONFIG[env as ENVIRONMENT];
    const entryBaseUrl = getHlvVanillaEntryBaseUrl(communityBaseUrl);
    const targetUrl = `${communityBaseUrl}/user`;
    const continueUrl = `${entryBaseUrl}/entry/register?Target=${encodeURIComponent(
      targetUrl,
    )}`;

    return `${identitySignupUrl}?application=community--entry-auth&continue=${encodeURIComponent(
      continueUrl,
    )}`;
  }
  return getAuthUrl('signup', queryParamValues);
}

export const NavBar: FunctionComponent = () => {
  const currentUrl = globalThis?.location?.href ?? window?.location?.href ?? '';
  const communityUrl = `${process.env.REACT_APP_COMMUNITY_BASE_URL}`;

  let updatedUrl;
  // temporary fix: conx-700
  if (currentUrl.includes('/settings')) {
    const url = new URL(currentUrl);
    url.searchParams.delete('errorCode');
    updatedUrl = `${url.origin}${url.pathname}${url.search}`;
  } else {
    updatedUrl = currentUrl;
  }

  const currentPage = encodeURIComponent(updatedUrl);
  const { accountId, name, picture, email } = useLoggedInUser() || {};
  const isLoggedIn = useIsLoggedIn();
  const normalizedUser: LoggedInUser = {
    id: accountId || '',
    publicName: name || '',
    avatar: picture,
  };

  const loginHref = useMemo(
    () =>
      getLoginUrl(
        {
          experience: 'entry-auth',
          email: email || '',
          redirectUrl: currentPage,
        },
        getEnvironment() as ENVIRONMENT,
      ),
    [currentPage],
  );
  const logoutHref = useMemo(() => {
    // CONX-892: When a user logs out from settings page, redirect them to the home page
    const redirectUrl = decodeURIComponent(currentPage).endsWith(
      '/user/settings',
    )
      ? encodeURIComponent(communityUrl)
      : currentPage;
    return getLogoutUrl(
      {
        experience: 'entry-auth',
        email: email || '',
        redirectUrl,
      },
      getEnvironment() as ENVIRONMENT,
    );
  }, [currentPage]);
  const signupHref = useMemo(
    () =>
      getSignupUrl(
        {
          experience: 'entry-auth',
          email: email || '',
          redirectUrl: currentPage,
        },
        getEnvironment() as ENVIRONMENT,
      ),
    [currentPage],
  );
  const switchHref = useMemo(
    () =>
      accountSwitcherIsActive()
        ? getLoginUrl(
            {
              redirectUrl: currentPage,
              experience: 'entry-auth',
              prompt: 'select_account',
            },
            getEnvironment() as ENVIRONMENT,
          )
        : '',
    [currentPage],
  );
  const appButtonHrefOverrides = useMemo(
    () => getHlvVanillaAppButtonHrefOverrides(getEnvironment() as ENVIRONMENT),
    [],
  );

  return (
    <>
      <CommunityNavigation
        currentApp={CommunityApp.PROFILE}
        isLoggedIn={isLoggedIn}
        user={normalizedUser}
        loginHref={loginHref}
        logoutHref={logoutHref}
        signupHref={signupHref}
        switchHref={switchHref}
        appButtonHrefOverrides={appButtonHrefOverrides}
        showNewMenu={newNavigationIsActive()}
        onAnalyticsSendEvent={(type, payload) =>
          CommunityAnalyticsClient.sendEvent(
            type as unknown as eventType,
            payload,
          )
        }
        email={email || ''}
      />
    </>
  );
};
